/**
 * CraneFoldRitualUI — client-side UI for the paper crane fold ritual
 * (Round 14 Phase A).
 *
 * Two-layer pattern:
 *   1. Pure state class (CraneFoldState) — no Phaser, testable in isolation.
 *   2. Phaser shell (CraneFoldRitualUI) — manages scene objects.
 *
 * Lifecycle:
 *   - Player initiates fold → sends MSG_CRANE_FOLD_START → server responds
 *     with MSG_CRANE_FOLD_STATE (active=true, startedAt, completesAt).
 *   - Progress bar fills over 30 seconds.
 *   - On completion, shows a text input for the crane message + recipient.
 *   - Player submits → sends MSG_CRANE_FOLD_RELEASE → server responds with
 *     MSG_CRANE_FOLD_STATE (active=false, error=null).
 */

import * as Phaser from 'phaser';
import type { Room } from 'colyseus.js';
import {
  MSG_CRANE_FOLD_START,
  MSG_CRANE_FOLD_CANCEL,
  MSG_CRANE_FOLD_RELEASE,
  MSG_CRANE_FOLD_STATE,
  CRANE_FOLD_DURATION_MS,
  CRANE_FOLD_MAX_MESSAGE_CHARS,
  COMBAT_PROTOCOL_VERSION,
  type CraneFoldStateEvent,
} from '@game/shared';

// ── Constants ──

const PANEL_DEPTH = 1010;
const BAR_W = 200;
const BAR_H = 12;
const BAR_Y_OFFSET = 60;
const TEXT_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  fontSize: '14px',
  color: '#f5f0e1',
  fontFamily: 'monospace',
  align: 'center',
};

// ── Pure state ──

export class CraneFoldState {
  active = false;
  startedAt: number | null = null;
  completesAt: number | null = null;
  craneId: string | null = null;
  error: string | null = null;
  /** True when fold timer is complete and we're awaiting message input. */
  awaitingMessage = false;

  apply(event: CraneFoldStateEvent): void {
    this.active = event.active;
    this.startedAt = event.startedAt;
    this.completesAt = event.completesAt;
    this.craneId = event.craneId;
    this.error = event.error;
    // If fold completed (active but completesAt in the past), show message input.
    if (this.active && this.completesAt !== null && Date.now() >= this.completesAt) {
      this.awaitingMessage = true;
    }
    if (!this.active) {
      this.awaitingMessage = false;
    }
  }

  getProgress(): number {
    if (!this.active || this.startedAt === null || this.completesAt === null) return 0;
    const elapsed = Date.now() - this.startedAt;
    const total = this.completesAt - this.startedAt;
    return Math.min(1, Math.max(0, elapsed / total));
  }

  isComplete(): boolean {
    if (!this.active || this.completesAt === null) return false;
    return Date.now() >= this.completesAt;
  }
}

// ── Phaser shell ──

export class CraneFoldRitualUI {
  private scene: Phaser.Scene;
  private room: Room;
  readonly state = new CraneFoldState();

  // Visual elements
  private container: Phaser.GameObjects.Container | null = null;
  private barBg: Phaser.GameObjects.Rectangle | null = null;
  private barFill: Phaser.GameObjects.Rectangle | null = null;
  private label: Phaser.GameObjects.Text | null = null;
  private messagePrompt: Phaser.GameObjects.Text | null = null;
  private typedText: Phaser.GameObjects.Text | null = null;

  // Update timer
  private updateTimer: Phaser.Time.TimerEvent | null = null;

  // Message capture state
  private captureActive = false;
  private currentMessage = '';
  private keyListener: ((e: KeyboardEvent) => void) | null = null;

  constructor(scene: Phaser.Scene, room: Room) {
    this.scene = scene;
    this.room = room;

    room.onMessage(MSG_CRANE_FOLD_STATE, (event: CraneFoldStateEvent) => {
      this.state.apply(event);
      this.refresh();
    });
  }

  /** Call from the scene to initiate a fold ritual. */
  startFold(): void {
    this.room.send(MSG_CRANE_FOLD_START, { v: COMBAT_PROTOCOL_VERSION });
  }

  /** Cancel an in-progress fold. */
  cancelFold(): void {
    this.room.send(MSG_CRANE_FOLD_CANCEL, { v: COMBAT_PROTOCOL_VERSION });
  }

  /** Release the completed crane with a message. */
  releaseCrane(message: string, recipientName: string | null, villageBroadcast: string | null): void {
    this.room.send(MSG_CRANE_FOLD_RELEASE, {
      v: COMBAT_PROTOCOL_VERSION,
      message,
      recipientName,
      villageBroadcast,
    });
  }

  private refresh(): void {
    if (this.state.active) {
      this.showProgressUI();
    } else {
      this.hideUI();
    }
  }

  private showProgressUI(): void {
    const cam = this.scene.cameras.main;
    const cx = cam.width / 2;
    const cy = cam.height / 2 + BAR_Y_OFFSET;

    if (!this.container) {
      this.container = this.scene.add.container(cx, cy);
      this.container.setScrollFactor(0);
      this.container.setDepth(PANEL_DEPTH);

      this.barBg = this.scene.add.rectangle(0, 0, BAR_W, BAR_H, 0x333333, 0.8);
      this.barFill = this.scene.add.rectangle(
        -BAR_W / 2, 0, 0, BAR_H, 0xd4a574, 1,
      ).setOrigin(0, 0.5);
      this.label = this.scene.add.text(0, -BAR_H - 6, 'Folding crane...', TEXT_STYLE)
        .setOrigin(0.5, 1);
      this.messagePrompt = this.scene.add.text(0, BAR_H + 8, '', TEXT_STYLE)
        .setOrigin(0.5, 0);
      this.typedText = this.scene.add.text(0, BAR_H + 32, '', {
        ...TEXT_STYLE,
        color: '#ffffff',
        wordWrap: { width: BAR_W },
      }).setOrigin(0.5, 0);

      this.container.add([this.barBg, this.barFill, this.label, this.messagePrompt, this.typedText]);
    }

    // Start update loop.
    if (!this.updateTimer) {
      this.updateTimer = this.scene.time.addEvent({
        delay: 100,
        loop: true,
        callback: () => this.updateProgress(),
      });
    }

    this.container.setVisible(true);
    this.updateProgress();
  }

  private updateProgress(): void {
    if (!this.barFill || !this.label || !this.messagePrompt) return;

    const progress = this.state.getProgress();
    this.barFill.width = BAR_W * progress;

    if (this.state.isComplete()) {
      this.label.setText('Crane folded!');
      if (!this.state.awaitingMessage) {
        this.state.awaitingMessage = true;
      }
      if (!this.captureActive) {
        this.startMessageCapture();
      }
      this.messagePrompt.setText('Write your message (up to 200 chars)\nEnter to send · Esc to cancel');
    } else {
      const remaining = this.state.completesAt
        ? Math.max(0, Math.ceil((this.state.completesAt - Date.now()) / 1000))
        : 0;
      this.label.setText(`Folding crane... ${remaining}s`);
      this.messagePrompt.setText('');
    }

    if (this.state.error) {
      this.messagePrompt.setText(this.state.error);
    }
  }

  private startMessageCapture(): void {
    if (this.captureActive) return;
    this.captureActive = true;
    this.currentMessage = '';
    if (this.typedText) this.typedText.setText('');

    this.keyListener = (e: KeyboardEvent) => {
      e.stopPropagation();

      if (e.key === 'Escape') {
        this.stopMessageCapture();
        this.cancelFold();
        return;
      }

      if (e.key === 'Enter') {
        const msg = this.currentMessage.trim();
        this.stopMessageCapture();
        this.releaseCrane(msg, null, null);
        return;
      }

      if (e.key === 'Backspace') {
        this.currentMessage = this.currentMessage.slice(0, -1);
        if (this.typedText) this.typedText.setText(this.currentMessage || ' ');
        return;
      }

      if (e.key.length === 1 && this.currentMessage.length < CRANE_FOLD_MAX_MESSAGE_CHARS) {
        this.currentMessage += e.key;
        if (this.typedText) this.typedText.setText(this.currentMessage);
      }
    };

    window.addEventListener('keydown', this.keyListener, { capture: true });
  }

  private stopMessageCapture(): void {
    if (!this.captureActive) return;
    this.captureActive = false;
    this.currentMessage = '';
    if (this.typedText) this.typedText.setText('');
    if (this.keyListener) {
      window.removeEventListener('keydown', this.keyListener, { capture: true });
      this.keyListener = null;
    }
  }

  private hideUI(): void {
    this.stopMessageCapture();
    if (this.updateTimer) {
      this.updateTimer.destroy();
      this.updateTimer = null;
    }
    if (this.container) {
      this.container.setVisible(false);
    }
  }

  destroy(): void {
    this.hideUI();
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
    this.barBg = null;
    this.barFill = null;
    this.label = null;
    this.messagePrompt = null;
    this.typedText = null;
  }
}
